// apps/web/src/lib/version-check.ts

import type { VersionMetadata, VersionCheckResult } from './types/version.types';

const VERSION_STORAGE_KEY = 'lov_app_version';

/**
 * Fetch the current version from the server.
 * Uses cache-busting headers to bypass CDN/browser cache.
 */
export async function fetchVersion(): Promise<VersionMetadata | null> {
  try {
    const response = await fetch('/version.json', {
      cache: 'no-cache',
      headers: {
        'Cache-Control': 'no-cache',
        'Pragma': 'no-cache',
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch version: ${response.status}`);
    }

    return (await response.json()) as VersionMetadata;
  } catch (error) {
    console.warn('[version-check] Version fetch failed:', error);
    return null;
  }
}

/**
 * Get the current version from localStorage or meta tags.
 */
export function getCurrentVersion(): VersionMetadata | null {
  // Try localStorage first
  const stored = localStorage.getItem(VERSION_STORAGE_KEY);
  if (stored) {
    try {
      return JSON.parse(stored) as VersionMetadata;
    } catch {
      // Invalid JSON, continue to meta tags
    }
  }

  // Fall back to meta tags (injected at build time)
  const versionMeta = document.querySelector('meta[name="app-version"]');
  const buildTimeMeta = document.querySelector('meta[name="build-time"]');
  const cacheVersionMeta = document.querySelector('meta[name="cache-version"]');

  if (versionMeta && buildTimeMeta && cacheVersionMeta) {
    const buildTime = buildTimeMeta.getAttribute('content') ?? new Date().toISOString();
    return {
      version: versionMeta.getAttribute('content') ?? '1.0.0',
      buildTime,
      buildTimestamp: new Date(buildTime).getTime(),
      gitCommit: 'unknown',
      gitBranch: 'unknown',
      cacheVersion: cacheVersionMeta.getAttribute('content') ?? 'v1',
    };
  }

  return null;
}

/**
 * Check if a new version is available.
 */
export async function checkForUpdate(): Promise<VersionCheckResult> {
  const current = getCurrentVersion();
  const latest = await fetchVersion();

  if (!current || !latest) {
    return { hasUpdate: false, currentVersion: current, newVersion: latest };
  }

  // Compare cache versions (most reliable, includes timestamp)
  const hasUpdate = current.cacheVersion !== latest.cacheVersion;

  return {
    hasUpdate,
    currentVersion: current,
    newVersion: latest,
  };
}

/**
 * Initialize version from server and store in localStorage.
 * Call this on app startup.
 */
export async function initializeVersion(): Promise<void> {
  const version = await fetchVersion();
  if (version) {
    localStorage.setItem(VERSION_STORAGE_KEY, JSON.stringify(version));
  }
}

/**
 * Force refresh to load new version.
 * Clears all caches and reloads the page.
 */
export async function forceRefresh(): Promise<void> {
  try {
    // Update version in localStorage before refresh
    const newVersion = await fetchVersion();
    if (newVersion) {
      localStorage.setItem(VERSION_STORAGE_KEY, JSON.stringify(newVersion));
    }

    // Unregister service workers if any exist
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map((reg) => reg.unregister()));
    }

    // Clear all browser caches
    if ('caches' in window) {
      const cacheNames = await caches.keys();
      await Promise.all(cacheNames.map((name) => caches.delete(name)));
    }

    // Hard reload the page
    window.location.reload();
  } catch (error) {
    console.error('[version-check] Failed to force refresh:', error);
    // Fallback: just reload
    window.location.reload();
  }
}
